import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const api = axios.create({
  baseURL: '/api/v1',
  headers: { 'Content-Type': 'application/json' }
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default function Integrations() {
  const { tenantId } = useParams();
  const [activeTab, setActiveTab] = useState('apikeys');
  const [apiKeys, setApiKeys] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [showWebhookModal, setShowWebhookModal] = useState(false);
  const [keyForm, setKeyForm] = useState({ name: '', permissions: ['read'], expiresAt: '' });
  const [webhookForm, setWebhookForm] = useState({ name: '', url: '', events: [] });

  const availableEvents = [
    'user.created', 'user.updated', 'user.deleted',
    'tenant.created', 'tenant.updated', 'tenant.suspended',
    'subscription.created', 'subscription.updated',
    'payment.succeeded', 'payment.failed'
  ];

  useEffect(() => {
    if (tenantId) {
      fetchApiKeys();
      fetchWebhooks();
    }
  }, [tenantId]);

  // Polling for real-time data updates
  useEffect(() => {
    const pollApiKeys = setInterval(() => {
      if (tenantId) fetchApiKeys();
    }, 15000); // 15 seconds

    const pollWebhooks = setInterval(() => {
      if (tenantId) fetchWebhooks();
    }, 15000); // 15 seconds

    return () => {
      clearInterval(pollApiKeys);
      clearInterval(pollWebhooks);
    };
  }, [tenantId]);

  const fetchApiKeys = async () => {
    try {
      const res = await api.get('/api-keys', { headers: { 'x-tenant-id': tenantId } });
      setApiKeys(res.data.data || []);
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWebhooks = async () => {
    try {
      const res = await api.get('/webhooks', { headers: { 'x-tenant-id': tenantId } });
      setWebhooks(res.data.data || []);
    } catch (error) {
      console.error('Failed to fetch webhooks:', error);
    }
  };

  const handleCreateKey = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api-keys', keyForm, { headers: { 'x-tenant-id': tenantId } });
      setShowKeyModal(false);
      setKeyForm({ name: '', permissions: ['read'], expiresAt: '' });
      fetchApiKeys();
    } catch (error) {
      alert('Failed to create API key');
    }
  };

  const handleDeleteKey = async (keyId) => {
    if (!window.confirm('Delete this API key?')) return;
    try {
      await api.delete(`/api-keys/${keyId}`, { headers: { 'x-tenant-id': tenantId } });
      fetchApiKeys();
    } catch (error) {
      alert('Failed to delete API key');
    }
  };

  const handleRegenerateKey = async (keyId) => {
    if (!window.confirm('Regenerate this API key? The old key will stop working.')) return;
    try {
      await api.post(`/api-keys/${keyId}/regenerate`, {}, { headers: { 'x-tenant-id': tenantId } });
      fetchApiKeys();
    } catch (error) {
      alert('Failed to regenerate API key');
    }
  };

  const handleCreateWebhook = async (e) => {
    e.preventDefault();
    try {
      await api.post('/webhooks', webhookForm, { headers: { 'x-tenant-id': tenantId } });
      setShowWebhookModal(false);
      setWebhookForm({ name: '', url: '', events: [] });
      fetchWebhooks();
    } catch (error) {
      alert('Failed to create webhook');
    }
  };

  const handleDeleteWebhook = async (webhookId) => {
    if (!window.confirm('Delete this webhook?')) return;
    try {
      await api.delete(`/webhooks/${webhookId}`, { headers: { 'x-tenant-id': tenantId } });
      fetchWebhooks();
    } catch (error) {
      alert('Failed to delete webhook');
    }
  };

  const handleTestWebhook = async (webhookId) => {
    try {
      const res = await api.post(`/webhooks/${webhookId}/test`, {}, { headers: { 'x-tenant-id': tenantId } });
      alert(res.data.success ? 'Test successful!' : 'Test failed: ' + res.data.message);
      fetchWebhooks();
    } catch (error) {
      alert('Failed to test webhook');
    }
  };

  const toggleEvent = (event) => {
    setWebhookForm(prev => ({
      ...prev,
      events: prev.events.includes(event)
        ? prev.events.filter(e => e !== event)
        : [...prev.events, event]
    }));
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Integrations</h1>

      <div className="flex border-b mb-6">
        <button
          onClick={() => setActiveTab('apikeys')}
          className={`px-4 py-2 ${activeTab === 'apikeys' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
        >
          API Keys
        </button>
        <button
          onClick={() => setActiveTab('webhooks')}
          className={`px-4 py-2 ${activeTab === 'webhooks' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
        >
          Webhooks
        </button>
      </div>

      {activeTab === 'apikeys' && (
        <div>
          <div className="flex justify-between mb-4">
            <h2 className="text-lg font-semibold">API Keys</h2>
            <button
              onClick={() => setShowKeyModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded"
            >
              + Create Key
            </button>
          </div>

          <div className="space-y-3">
            {loading ? (
              <p>Loading...</p>
            ) : apiKeys.length === 0 ? (
              <p className="text-gray-500">No API keys. Create one to get started.</p>
            ) : (
              apiKeys.map(key => (
                <div key={key.keyId} className="bg-white p-4 rounded shadow">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium">{key.name}</div>
                      <div className="text-sm font-mono bg-gray-100 p-2 rounded mt-1">{key.key}</div>
                      <div className="text-sm text-gray-500 mt-1">
                        Permissions: {key.permissions?.join(', ')} | 
                        Created: {new Date(key.createdAt).toLocaleDateString()}
                        {key.lastUsedAt && ` | Last used: ${new Date(key.lastUsedAt).toLocaleDateString()}`}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleRegenerateKey(key.keyId)}
                        className="text-yellow-600 hover:underline text-sm"
                      >
                        Regenerate
                      </button>
                      <button
                        onClick={() => handleDeleteKey(key.keyId)}
                        className="text-red-600 hover:underline text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {activeTab === 'webhooks' && (
        <div>
          <div className="flex justify-between mb-4">
            <h2 className="text-lg font-semibold">Webhooks</h2>
            <button
              onClick={() => setShowWebhookModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded"
            >
              + Add Webhook
            </button>
          </div>

          <div className="space-y-3">
            {loading ? (
              <p>Loading...</p>
            ) : webhooks.length === 0 ? (
              <p className="text-gray-500">No webhooks. Add one to receive event notifications.</p>
            ) : (
              webhooks.map(wh => (
                <div key={wh.webhookId} className="bg-white p-4 rounded shadow">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium">{wh.name}</div>
                      <div className="text-sm text-gray-500">{wh.url}</div>
                      <div className="text-sm mt-1">Events: {wh.events?.join(', ')}</div>
                      <div className="text-sm text-gray-500">
                        Status: {wh.isActive ? 'Active' : 'Inactive'}
                        {wh.lastStatus && ` | Last: ${wh.lastStatus}`}
                        {wh.lastTriggeredAt && ` | ${new Date(wh.lastTriggeredAt).toLocaleDateString()}`}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleTestWebhook(wh.webhookId)}
                        className="text-blue-600 hover:underline text-sm"
                      >
                        Test
                      </button>
                      <button
                        onClick={() => handleDeleteWebhook(wh.webhookId)}
                        className="text-red-600 hover:underline text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {showKeyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto py-6">
          <div className="bg-white p-6 rounded-lg w-full max-w-md my-6">
            <h2 className="text-xl font-bold mb-4">Create API Key</h2>
            <form onSubmit={handleCreateKey}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Name</label>
                <input
                  type="text"
                  value={keyForm.name}
                  onChange={(e) => setKeyForm({ ...keyForm, name: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  placeholder="My API Key"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Permissions</label>
                <div className="flex gap-4">
                  {['read', 'write', 'admin'].map(perm => (
                    <label key={perm} className="flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={keyForm.permissions.includes(perm)}
                        onChange={(e) => {
                          setKeyForm(prev => ({
                            ...prev,
                            permissions: e.target.checked
                              ? [...prev.permissions, perm]
                              : prev.permissions.filter(p => p !== perm)
                          }));
                        }}
                      />
                      {perm}
                    </label>
                  ))}
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Expires (optional)</label>
                <input
                  type="date"
                  value={keyForm.expiresAt}
                  onChange={(e) => setKeyForm({ ...keyForm, expiresAt: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowKeyModal(false)} className="px-4 py-2 border rounded">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showWebhookModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Add Webhook</h2>
            <form onSubmit={handleCreateWebhook}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Name *</label>
                <input
                  type="text"
                  value={webhookForm.name}
                  onChange={(e) => setWebhookForm({ ...webhookForm, name: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">URL *</label>
                <input
                  type="url"
                  value={webhookForm.url}
                  onChange={(e) => setWebhookForm({ ...webhookForm, url: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  placeholder="https://..."
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Events *</label>
                <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                  {availableEvents.map(event => (
                    <label key={event} className="flex items-center gap-1 text-sm">
                      <input
                        type="checkbox"
                        checked={webhookForm.events.includes(event)}
                        onChange={() => toggleEvent(event)}
                      />
                      {event}
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowWebhookModal(false)} className="px-4 py-2 border rounded">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">
                  Add
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}